import React, { useEffect, useState } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import {
  GetAllFollowUpTypes,
  fetchProjectData,
  GetLeadLog,
  createUserTask,
  sendEmailWithAttachments,
  GetUserTasks,
  UserTaskList,
} from "../Services/APIService";
import selectStyles from "../js/selectStyles.js";

interface LeadDetailsViewProps {
  leadId: number;
  onTaskCreated: () => void;
}

export interface followupTypeList {
  id: number;
  name: string;
  isActive: boolean;
}

export interface Project {
  projectID: number;
  projectName: string;
}

interface SelectOption {
  value: number;
  label: string;
  projectData?: Project;
}

interface FollowUpTypeOption {
  value: number;
  label: string;
}

interface FollowUpFormData {
  reportTo: SelectOption | null;
  followupDate: string;
  followUpType: FollowUpTypeOption | null;
  note: string;
}

interface SimpleFollowUpFormData {
  project: SelectOption | null;
  followupDate: string;
  note: string;
}

interface NoteFormData {
  note: string;
}

interface FollowUpDetails {
  id: number;
  followUpTypeId: number;
  projectId: number;
  createdDate: string;
  createdBy: string;
  note: string;
}

interface CreateUserTaskRequest {
  leadId: number;
  logId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  tenantId: number;
  createdBy: number;
  // optional fields for some flows
  isCompleted?: boolean;
  taskCompletionDate?: string;
  reminderDate?: string;
}

interface LeadLgoList {
  logId: number;
  leadId: number;
  isNextActionRequired: boolean;
  taskId: number | null;
  assignedUser: number;
}

const UserTaskDashboard: React.FC<LeadDetailsViewProps> = ({
  leadId,
  onTaskCreated,
}) => {
  const [activeTab, setActiveTab] = useState<string>("note");
  const [followupType, setFollowupType] = useState<followupTypeList[]>([]);
  const [projectData, setprojectData] = useState<Project[]>([]);
  const [projectOption, setProjectOption] = useState<SelectOption[]>([]);
  const [followUpTypeOption, setFollowUpTypeOption] = useState<
    FollowUpTypeOption[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [followUpFormData, setFollowUpFormData] = useState<FollowUpFormData>({
    reportTo: null,
    followupDate: "",
    followUpType: null,
    note: "",
  });
  const [noteFormData, setNoteFormData] = useState<NoteFormData>({ note: "" });
  const [followUpDetails, setFollowUpDetails] = useState<FollowUpDetails[]>([]);
  const [leadLogData, setLeadLogData] = useState<LeadLgoList[]>([]);

  // NEW: user tasks and computed project options for Builder Meeting / Booking
  const [userTasks, setUserTasks] = useState<UserTaskList[]>([]);

  // NEW: separate form state for Builder Meeting, Booking, SiteVisited
  const [builderMeetingFormData, setBuilderMeetingFormData] =
    useState<SimpleFollowUpFormData>({
      project: null,
      followupDate: "",
      note: "",
    });

  const [bookingFormData, setBookingFormData] =
    useState<SimpleFollowUpFormData>({
      project: null,
      followupDate: "",
      note: "",
    });

  const [siteVisitedFormData, setSiteVisitedFormData] =
    useState<SimpleFollowUpFormData>({
      project: null,
      followupDate: "",
      note: "",
    });

  // Email form
  const [emailForm, setEmailForm] = useState({
    toEmail: "",
    ccEmail: "",
    subject: "",
    message: "",
    attachments: [] as File[],
  });
  const [isEmailSending, setIsEmailSending] = useState(false);

  // More dropdown show state (for hover)
  const [showMore, setShowMore] = useState(false);

  // ========= INITIAL LOAD =========
  useEffect(() => {
    const getFetchpMasteData = async () => {
      setIsLoading(true);
      try {
        // Fetch follow-up types, projects and user tasks (filtered by lead)
        const [fetchAllFollowUpType, fetchAllProjectData, tasks] =
          await Promise.all([
            GetAllFollowUpTypes(),
            fetchProjectData(),
            GetUserTasks(leadId),
          ]);

        // Only active types, not id 0, not "note"
        const activeTypes: followupTypeList[] = fetchAllFollowUpType.filter(
          (type: followupTypeList) =>
            type.isActive && type.id !== 0 && type.name.toLowerCase() !== "note"
        );

        setFollowupType(activeTypes);

        // For "Schedule Follow-up" tab, only show types with id < 10
        const scheduleFollowUpTypes = activeTypes.filter((t) => t.id < 10);
        const followUpOptions: FollowUpTypeOption[] = scheduleFollowUpTypes.map(
          (type) => ({
            value: type.id,
            label: type.name,
          })
        );
        setFollowUpTypeOption(followUpOptions);

        const options = fetchAllProjectData.map((project: any) => ({
          value: project.projectID,
          label: project.projectName,
          projectData: project,
        }));
        setProjectOption(options);
        setprojectData(fetchAllProjectData);

        setUserTasks(tasks);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch initial data.");
      } finally {
        setIsLoading(false);
      }
    };

    getFetchpMasteData();
  }, [leadId]);

  // ========= DERIVED PROJECT OPTIONS FOR BUILDER MEETING & BOOKING =========
  useEffect(() => {
    const leadTasks = userTasks.filter((t) => t.leadId === leadId);

    // Create a map for id -> name from project master
    const projectNameMap = new Map<number, string>();
    projectData.forEach((p) => projectNameMap.set(p.projectID, p.projectName));

    // --- Builder Meeting projects ---
    const siteVisitTasks = leadTasks.filter(
      (t) => t.followupTypeId === 6 && t.isCompleted
    );
    const builderMeetingTasks = leadTasks.filter(
      (t) => t.followupTypeId === 11
    );

    const builderMeetingProjectsSet = new Set(
      builderMeetingTasks.map((t) => t.projectId)
    );

    const bmOptionMap = new Map<number, SelectOption>();

    siteVisitTasks.forEach((t) => {
      const pid = t.projectId;
      if (!pid) return;
      if (!builderMeetingProjectsSet.has(pid)) {
        if (!bmOptionMap.has(pid)) {
          const label =
            projectNameMap.get(pid) || t.projectName || `Project #${pid}`;
          bmOptionMap.set(pid, { value: pid, label });
        }
      }
    });

    // --- Booking projects ---
    const builderMeetingCompletedTasks = leadTasks.filter(
      (t) => t.followupTypeId === 11 && t.isCompleted
    );
    const bookingTasks = leadTasks.filter((t) => t.followupTypeId === 12);

    const bookingProjectsSet = new Set(bookingTasks.map((t) => t.projectId));
    const bookingOptionMap = new Map<number, SelectOption>();

    builderMeetingCompletedTasks.forEach((t) => {
      const pid = t.projectId;
      if (!pid) return;
      if (!bookingProjectsSet.has(pid)) {
        if (!bookingOptionMap.has(pid)) {
          const label =
            projectNameMap.get(pid) || t.projectName || `Project #${pid}`;
          bookingOptionMap.set(pid, { value: pid, label });
        }
      }
    });
  }, [userTasks, projectData, leadId]);

  // ========= HELPERS =========
  const getLocalStorageValue = (key: string): number => {
    const value = localStorage.getItem(key);
    return value ? parseInt(value, 10) : 0;
  };

  const handleFollowUpSelectChange = (
    name: string,
    selectedOption: SelectOption | FollowUpTypeOption | null
  ) => {
    setFollowUpFormData((prev) => ({
      ...prev,
      [name]: selectedOption as any,
    }));
  };

  const handleFollowUpInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFollowUpFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNoteInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNoteFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBuilderMeetingProjectChange = (option: SelectOption | null) => {
    setBuilderMeetingFormData((prev) => ({
      ...prev,
      project: option,
    }));
  };

  const handleBookingProjectChange = (option: SelectOption | null) => {
    setBookingFormData((prev) => ({
      ...prev,
      project: option,
    }));
  };

  const handleSiteVisitedProjectChange = (option: SelectOption | null) => {
    setSiteVisitedFormData((prev) => ({
      ...prev,
      project: option,
    }));
  };

  const handleBuilderMeetingNoteChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setBuilderMeetingFormData((prev) => ({
      ...prev,
      note: value,
    }));
  };

  const handleBookingNoteChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setBookingFormData((prev) => ({
      ...prev,
      note: value,
    }));
  };

  const handleSiteVisitedNoteChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setSiteVisitedFormData((prev) => ({
      ...prev,
      note: value,
    }));
  };

  // ========= SUBMIT =========
  const handleSubmit = async () => {
    // Validation per tab
    if (activeTab === "followup") {
      const isProjectRequired = ![2, 3, 4, 5].includes(
        followUpFormData.followUpType?.value ?? -1
      );
      if (
        !followUpFormData.followUpType ||
        !followUpFormData.followupDate ||
        (isProjectRequired && !followUpFormData.reportTo)
      ) {
        toast.error(
          "Please select a project and ensure all fields are filled for Follow-up"
        );
        return;
      }
    } else if (activeTab === "note") {
      if (!noteFormData.note.trim()) {
        toast.error("Please fill in the note");
        return;
      }
    } else if (activeTab === "builderMeeting") {
      if (
        !builderMeetingFormData.project ||
        !builderMeetingFormData.followupDate
      ) {
        toast.error(
          "Please select a project and fill all fields for Builder Meeting"
        );
        return;
      }
    } else if (activeTab === "booking") {
      if (
        !bookingFormData.project ||
        !bookingFormData.followupDate ||
        !bookingFormData.note.trim()
      ) {
        toast.error("Please select a project and fill all fields for Booking");
        return;
      }
    } else if (activeTab === "siteVisited") {
      // NEW: site visited validation
      if (
        !siteVisitedFormData.project ||
        !siteVisitedFormData.followupDate ||
        !siteVisitedFormData.note.trim()
      ) {
        toast.error(
          "Please select a project, date and enter a note for Site Visit"
        );
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const assignedUser =
        getLocalStorageValue("assignedUser") || getLocalStorageValue("UserId");
      const tenantId = getLocalStorageValue("tenantId");
      const createdBy =
        getLocalStorageValue("createdBy") || getLocalStorageValue("UserId");

      const currentDateTime = new Date()
        .toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
        .replace(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+):(\d+)/, "$3-$2-$1T$4:$5:$6");

      const leadLogs = await GetLeadLog();
      const allowCreateTaskLead = leadLogs.filter(
        (log: any) => log.isNextActionRequired === true && log.taskId === null
      );
      setLeadLogData(leadLogs);
      const selectedLeadLogData = allowCreateTaskLead.find(
        (x: any) => x.leadId === leadId
      );

      // Build requestData based on activeTab
      const baseData: Partial<CreateUserTaskRequest> = {
        leadId: leadId,
        logId: selectedLeadLogData ? selectedLeadLogData.logId : 0,
        assignedUser: assignedUser,
        tenantId: tenantId,
        createdBy: createdBy,
      };

      let requestData: CreateUserTaskRequest;

      if (activeTab === "followup") {
        requestData = {
          ...(baseData as CreateUserTaskRequest),
          projectId: followUpFormData.reportTo?.value ?? 0,
          followupTypeId: followUpFormData.followUpType?.value ?? 0,
          followupDate: followUpFormData.followupDate,
          taskNotes: followUpFormData.note,
        };
      } else if (activeTab === "note") {
        requestData = {
          ...(baseData as CreateUserTaskRequest),
          projectId: 0,
          followupTypeId: 1, // Note type
          followupDate: currentDateTime,
          taskNotes: noteFormData.note,
        };
      } else if (activeTab === "builderMeeting") {
        requestData = {
          ...(baseData as CreateUserTaskRequest),
          projectId: builderMeetingFormData.project?.value ?? 0,
          followupTypeId: 11, // Builder Meeting
          followupDate: builderMeetingFormData.followupDate,
          taskNotes: builderMeetingFormData.note,
        };
      } else if (activeTab === "booking") {
        requestData = {
          ...(baseData as CreateUserTaskRequest),
          projectId: bookingFormData.project?.value ?? 0,
          followupTypeId: 12, // Booking
          followupDate: bookingFormData.followupDate,
          taskNotes: bookingFormData.note,
        };
      } else {
        // siteVisited
        requestData = {
          ...(baseData as CreateUserTaskRequest),
          projectId: siteVisitedFormData.project?.value ?? 0,
          followupTypeId: 6, // Site Visit
          followupDate: siteVisitedFormData.followupDate,
          taskNotes: siteVisitedFormData.note,
          isCompleted: true,
          taskCompletionDate: siteVisitedFormData.followupDate,
        };
      }

      const result = await createUserTask(requestData as any);

      if (result.isCreated) {
        const label =
          activeTab === "followup"
            ? "Follow-up"
            : activeTab === "note"
              ? "Note"
              : activeTab === "builderMeeting"
                ? "Builder Meeting"
                : activeTab === "booking"
                  ? "Booking"
                  : "Site Visit";

        toast.success(`${label} created successfully!`);

        // Update local follow-up list (optional)
        if (activeTab === "followup") {
          setFollowUpDetails((prev) => [
            ...prev,
            {
              id: result.id || Date.now(),
              followUpTypeId: followUpFormData.followUpType?.value ?? 0,
              projectId: followUpFormData.reportTo?.value ?? 0,
              createdDate: followUpFormData.followupDate,
              createdBy: createdBy.toString(),
              note: followUpFormData.note,
            },
          ]);
          setFollowUpFormData({
            reportTo: null,
            followupDate: "",
            followUpType: null,
            note: "",
          });
        } else if (activeTab === "note") {
          setNoteFormData({ note: "" });
        } else if (activeTab === "builderMeeting") {
          setFollowUpDetails((prev) => [
            ...prev,
            {
              id: result.id || Date.now(),
              followUpTypeId: 11,
              projectId: builderMeetingFormData.project?.value ?? 0,
              createdDate: builderMeetingFormData.followupDate,
              createdBy: createdBy.toString(),
              note: builderMeetingFormData.note,
            },
          ]);
          setBuilderMeetingFormData({
            project: null,
            followupDate: "",
            note: "",
          });
        } else if (activeTab === "booking") {
          setFollowUpDetails((prev) => [
            ...prev,
            {
              id: result.id || Date.now(),
              followUpTypeId: 12,
              projectId: bookingFormData.project?.value ?? 0,
              createdDate: bookingFormData.followupDate,
              createdBy: createdBy.toString(),
              note: bookingFormData.note,
            },
          ]);
          setBookingFormData({ project: null, followupDate: "", note: "" });
        } else if (activeTab === "siteVisited") {
          setFollowUpDetails((prev) => [
            ...prev,
            {
              id: result.id || Date.now(),
              followUpTypeId: 6,
              projectId: siteVisitedFormData.project?.value ?? 0,
              createdDate: siteVisitedFormData.followupDate,
              createdBy: createdBy.toString(),
              note: siteVisitedFormData.note,
            },
          ]);
          setSiteVisitedFormData({ project: null, followupDate: "", note: "" });
        }

        // Refresh user tasks so the Builder Meeting / Booking options update
        const freshTasks = await GetUserTasks(leadId);
        setUserTasks(freshTasks);
      } else {
        toast.error(result.message || "Failed to create task.");
      }

      onTaskCreated();
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Error creating task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========= DATE HELPERS =========
  const formatDateTimeLocal = (date: Date): string => {
    return date
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+)/, "$3-$2-$1T$4:$5");
  };

  useEffect(() => {
    const now = new Date();
    if (activeTab === "followup" && !followUpFormData.followupDate) {
      setFollowUpFormData((prev) => ({
        ...prev,
        followupDate: formatDateTimeLocal(now),
      }));
    } else if (
      activeTab === "builderMeeting" &&
      !builderMeetingFormData.followupDate
    ) {
      setBuilderMeetingFormData((prev) => ({
        ...prev,
        followupDate: formatDateTimeLocal(now),
      }));
    } else if (activeTab === "booking" && !bookingFormData.followupDate) {
      setBookingFormData((prev) => ({
        ...prev,
        followupDate: formatDateTimeLocal(now),
      }));
    } else if (
      activeTab === "siteVisited" &&
      !siteVisitedFormData.followupDate
    ) {
      setSiteVisitedFormData((prev) => ({
        ...prev,
        followupDate: formatDateTimeLocal(now),
      }));
    }
  }, [activeTab]);

  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Apply validation for ALL except siteVisited
    if (activeTab !== "siteVisited") {
      const selectedDateTime = new Date(value);
      const currentDateTime = new Date();

      if (selectedDateTime < currentDateTime) {
        toast.error("Please select a future date and time");
        return;
      }
    }

    // Update form data based on active tab
    if (activeTab === "followup") {
      setFollowUpFormData((prev) => ({
        ...prev,
        followupDate: value,
      }));
    } else if (activeTab === "builderMeeting") {
      setBuilderMeetingFormData((prev) => ({
        ...prev,
        followupDate: value,
      }));
    } else if (activeTab === "booking") {
      setBookingFormData((prev) => ({
        ...prev,
        followupDate: value,
      }));
    } else if (activeTab === "siteVisited") {
      setSiteVisitedFormData((prev) => ({
        ...prev,
        followupDate: value,
      }));
    }
  };

  // ========= EMAIL HANDLERS (unchanged) =========
  const handleEmailInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    setEmailForm((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
    e.currentTarget.value = "";
  };

  const removeAttachment = (index: number) => {
    setEmailForm((prev) => {
      const newFiles = [...prev.attachments];
      newFiles.splice(index, 1);
      return { ...prev, attachments: newFiles };
    });
  };

  const validateEmailForm = (): boolean => {
    if (!emailForm.toEmail.trim()) {
      toast.error("Please enter at least one recipient in To.");
      return false;
    }
    return true;
  };

  const handleSendEmail = async () => {
    if (!validateEmailForm()) return;
    setIsEmailSending(true);
    try {
      const tenantId = getLocalStorageValue("tenantId");
      const userId = getLocalStorageValue("UserId");
      const form = new FormData();
      form.append("ToEmail", emailForm.toEmail);
      if (emailForm.ccEmail) form.append("CcEmail", emailForm.ccEmail);
      form.append("Subject", emailForm.subject || "");
      form.append("Message", emailForm.message || "");
      // append attachments (same as before)
      emailForm.attachments.forEach((file) => {
        form.append("Attachments", file, file.name);
      });

      // <-- NEW: append leadId (note: leadId is the prop sent into this component)
      form.append("LeadId", String(leadId));
      form.append("UserId", String(userId));
      form.append("TenantId", String(tenantId));

      const result = await sendEmailWithAttachments(form);

      if (result && result.success) {
        toast.success(result.message || "Email sent successfully.");
        setEmailForm({
          toEmail: "",
          ccEmail: "",
          subject: "",
          message: "",
          attachments: [],
        });

        const freshTasks = await GetUserTasks(leadId);
        setUserTasks(freshTasks);
      } else {
        toast.error(result?.message || "Failed to send email.");
      }
    } catch (err: any) {
      console.error("Send email error:", err);
      const msg =
        err?.response?.data?.message || err?.message || "Failed to send email.";
      toast.error(msg);
    } finally {
      setIsEmailSending(false);
    }
  };

  // ========= UI =========
  // We'll show only first 4 primary tabs; others go into "More" dropdown on hover
  const primaryTabs = [
    { key: "note", label: "Note", icon: "bi bi-quote" },
    { key: "followup", label: "Schedule Follow-up", icon: "bi bi-stopwatch" },
    {
      key: "siteVisited",
      label: "Site Visited",
      icon: "bi bi-binoculars",
    },
    { key: "builderMeeting", label: "Builder Meeting", icon: "bi bi-people" },
  ];

  const hiddenTabs = [
    { key: "booking", label: "Booking", icon: "bi bi-journal-check" },
    { key: "email", label: "Email", icon: "bi bi-envelope" },
  ];

  return (
    <div className="card">
      <ul
        className="nav nav-tabs cardHeaderHegiht"
        id="myTab"
        role="tablist"
        style={{ borderBottom: "1px solid rgb(222 226 230)" }}
      >
        {primaryTabs.map((t) => (
          <li className="nav-item" role="presentation" key={t.key}>
            <button
              className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHead ${activeTab === t.key ? "active" : ""
                }`}
              id={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              type="button"
              role="tab"
              aria-controls={`tab-${t.key}-pane`}
              aria-selected={activeTab === t.key}
            >
              <i className={t.icon}></i> {t.label}
            </button>
          </li>
        ))}

        {/* More dropdown for hidden tabs (shows on hover) */}
        <li
          className="nav-item dropdown ms-auto"
          style={{ flexShrink: 0, position: "relative" }}
          onMouseEnter={() => setShowMore(true)}
          onMouseLeave={() => setShowMore(false)}
        >
          <button
            className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHead ${showMore ? "show" : ""
              }`}
            id="tab-more"
            type="button"
            aria-expanded={showMore}
            style={{ whiteSpace: "nowrap" }}
          >
            <i className="bi bi-three-dots"></i>
          </button>

          {showMore && (
            <ul
              style={{
                position: "absolute",
                right: 0,
                top: "100%",
                zIndex: 9999,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                minWidth: "180px",
                padding: "6px 0",
                listStyle: "none",
                margin: "4px 0 0 0",
              }}
            >
              {hiddenTabs.map((t) => (
                <li key={t.key}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab(t.key);
                      setShowMore(false);
                    }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 16px",
                      border: "none",
                      background: activeTab === t.key ? "#f5eef0" : "transparent",
                      color: activeTab === t.key ? "rgb(167, 133, 144)" : "#33475b",
                      fontSize: "13px",
                      fontWeight: activeTab === t.key ? 600 : 400,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8f4f5")
                    }
                    onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      activeTab === t.key ? "#f5eef0" : "transparent")
                    }
                  >
                    <i className={t.icon} style={{ fontSize: "14px" }}></i>
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </li>
      </ul>

      <div className="tab-content card-body pb-0" id="myTabContent">
        {/* Follow-up pane */}
        <div
          className={`tab-pane fade ${activeTab === "followup" ? "show active" : ""
            }`}
          id="tab-followup-pane"
          role="tabpanel"
          aria-labelledby="tab-followup"
          tabIndex={0}
        >
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label className="form-label">
                Schedule Follow-up Type <span className="text-danger">*</span>
              </label>
              <Select
                options={followUpTypeOption}
                value={followUpFormData.followUpType}
                onChange={(option) =>
                  handleFollowUpSelectChange("followUpType", option)
                }
                className="basic-single-select"
                classNamePrefix="select"
                placeholder="Select follow-up type"
                isLoading={isLoading}
                isClearable
                isSearchable
                noOptionsMessage={() => "No menu items available"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label className="form-label">Select Project</label>
              <Select
                options={projectOption}
                value={followUpFormData.reportTo}
                onChange={(option) =>
                  handleFollowUpSelectChange("reportTo", option)
                }
                className="basic-single-select"
                classNamePrefix="select"
                placeholder="Select project"
                isLoading={isLoading}
                isClearable
                isSearchable
                noOptionsMessage={() => "No menu items available"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="followupDate" className="form-label">
                Schedule on <span className="text-danger">*</span>
              </label>
              <input
                type="datetime-local"
                id="followupDate"
                name="followupDate"
                className="form-control form-control-lg"
                value={followUpFormData.followupDate}
                onChange={handleDateTimeChange}
                min={new Date().toISOString().slice(0, 16)}
                required
              />
            </div>

            <div className="col-12 mb-3">
              <label htmlFor="note" className="form-label">
                Task Note
              </label>
              <textarea
                onChange={handleFollowUpInputChange}
                id="note"
                name="note"
                className="form-control form-control-lg"
                placeholder="Enter Remarks"
                value={followUpFormData.note}
                rows={4}
                required
              />
            </div>
          </div>

          <div className="card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Follow-up"}
            </button>
          </div>
        </div>

        {/* Note pane */}
        <div
          className={`tab-pane fade ${activeTab === "note" ? "show active" : ""
            }`}
          id="tab-note-pane"
          role="tabpanel"
          aria-labelledby="tab-note"
          tabIndex={0}
        >
          <div className="row">
            <div className="col-12 mb-3">
              <label htmlFor="note" className="form-label">
                Note <span className="text-danger">*</span>
              </label>
              <textarea
                onChange={handleNoteInputChange}
                id="note"
                name="note"
                className="form-control form-control-lg"
                placeholder="Enter Note"
                value={noteFormData.note}
                rows={4}
                required
              />
            </div>
          </div>

          <div className="card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Note"}
            </button>
          </div>
        </div>

        {/* Site Visited pane */}
        <div
          className={`tab-pane fade ${activeTab === "siteVisited" ? "show active" : ""
            }`}
          id="tab-siteVisited-pane"
          role="tabpanel"
          aria-labelledby="tab-siteVisited"
          tabIndex={0}
        >
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label className="form-label">
                Select Project <span className="text-danger">*</span>
              </label>
              {/* show all projects and make mandatory */}
              <Select
                options={projectOption}
                value={siteVisitedFormData.project}
                onChange={handleSiteVisitedProjectChange}
                className="basic-single-select"
                classNamePrefix="select"
                placeholder="Select project"
                isLoading={isLoading}
                isClearable={false}
                isSearchable
                noOptionsMessage={() => "No menu items available"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="siteVisitedDate" className="form-label">
                Site Visited Date <span className="text-danger">*</span>
              </label>
              <input
                type="datetime-local"
                id="siteVisitedDate"
                name="followupDate"
                className="form-control form-control-lg"
                value={siteVisitedFormData.followupDate}
                onChange={handleDateTimeChange}
                required
              />
            </div>

            <div className="col-12 mb-3">
              <label htmlFor="siteVisitedNote" className="form-label">
                Note <span className="text-danger">*</span>
              </label>
              <textarea
                id="siteVisitedNote"
                className="form-control form-control-lg"
                placeholder="Enter Site Visit details"
                value={siteVisitedFormData.note}
                onChange={handleSiteVisitedNoteChange}
                rows={4}
                required
              />
            </div>
          </div>

          <div className="card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Site Visit"}
            </button>
          </div>
        </div>

        {/* Builder Meeting pane */}
        <div
          className={`tab-pane fade ${activeTab === "builderMeeting" ? "show active" : ""
            }`}
          id="tab-builderMeeting-pane"
          role="tabpanel"
          aria-labelledby="tab-builderMeeting"
          tabIndex={0}
        >
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label className="form-label">
                Select Project <span className="text-danger">*</span>
              </label>
              <Select
                options={projectOption}
                value={builderMeetingFormData.project}
                onChange={handleBuilderMeetingProjectChange}
                className="basic-single-select"
                classNamePrefix="select"
                placeholder="Select project"
                isLoading={isLoading}
                isClearable
                isSearchable
                noOptionsMessage={() => "No menu items available"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="builderMeetingDate" className="form-label">
                Schedule Meeting Date <span className="text-danger">*</span>
              </label>
              <input
                type="datetime-local"
                id="builderMeetingDate"
                name="followupDate"
                className="form-control form-control-lg"
                value={builderMeetingFormData.followupDate}
                onChange={handleDateTimeChange}
                min={new Date().toISOString().slice(0, 16)}
                required
              />
            </div>

            <div className="col-12 mb-3">
              <label htmlFor="builderMeetingNote" className="form-label">
                Note
              </label>
              <textarea
                id="builderMeetingNote"
                className="form-control form-control-lg"
                placeholder="Enter Builder Meeting details"
                value={builderMeetingFormData.note}
                onChange={handleBuilderMeetingNoteChange}
                rows={4}
                required
              />
            </div>
          </div>

          <div className="card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Builder Meeting"}
            </button>
          </div>
        </div>

        {/* Booking pane */}
        <div
          className={`tab-pane fade ${activeTab === "booking" ? "show active" : ""
            }`}
          id="tab-booking-pane"
          role="tabpanel"
          aria-labelledby="tab-booking"
          tabIndex={0}
        >
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label className="form-label">
                Select Project <span className="text-danger">*</span>
              </label>
              <Select
                options={projectOption}
                value={bookingFormData.project}
                onChange={handleBookingProjectChange}
                className="basic-single-select"
                classNamePrefix="select"
                placeholder="Select project"
                isLoading={isLoading}
                isClearable
                isSearchable
                noOptionsMessage={() => "No menu items available"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="bookingDate" className="form-label">
                Booking Date <span className="text-danger">*</span>
              </label>
              <input
                type="datetime-local"
                id="bookingDate"
                name="followupDate"
                className="form-control form-control-lg"
                value={bookingFormData.followupDate}
                onChange={handleDateTimeChange}
                min={new Date().toISOString().slice(0, 16)}
                required
              />
            </div>

            <div className="col-12 mb-3">
              <label htmlFor="bookingNote" className="form-label">
                Note <span className="text-danger">*</span>
              </label>
              <textarea
                id="bookingNote"
                className="form-control form-control-lg"
                placeholder="Enter Booking details"
                value={bookingFormData.note}
                onChange={handleBookingNoteChange}
                rows={4}
                required
              />
            </div>
          </div>

          <div className="card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Booking"}
            </button>
          </div>
        </div>

        {/* Email pane */}
        <div
          className={`tab-pane fade ${activeTab === "email" ? "show active" : ""
            }`}
          id="tab-email-pane"
          role="tabpanel"
          aria-labelledby="tab-email"
          tabIndex={0}
        >
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label className="form-label">
                To <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="toEmail"
                value={emailForm.toEmail}
                onChange={handleEmailInputChange}
                className="form-control form-control-lg"
                placeholder="recipient1@example.com, recipient2@example.com"
              />
              <small className="text-muted">
                Separate multiple addresses by comma
              </small>
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label className="form-label">Cc</label>
              <input
                type="text"
                name="ccEmail"
                value={emailForm.ccEmail}
                onChange={handleEmailInputChange}
                className="form-control form-control-lg"
                placeholder="cc@example.com, cc2@example.com"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Subject</label>
              <input
                type="text"
                name="subject"
                value={emailForm.subject}
                onChange={handleEmailInputChange}
                className="form-control form-control-lg"
                placeholder="Subject"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Message</label>
              <textarea
                name="message"
                value={emailForm.message}
                onChange={handleEmailInputChange}
                className="form-control form-control-lg"
                placeholder="Write your message here (HTML allowed)"
                rows={8}
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Attachments</label>
              <div className="mb-2">
                <input
                  type="file"
                  multiple
                  onChange={handleAttachmentChange}
                  className="form-control"
                />
              </div>

              {emailForm.attachments.length > 0 && (
                <div className="attachment-list">
                  <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                    {emailForm.attachments.map((file, idx) => (
                      <li key={idx} className="d-flex align-items-center mb-2">
                        <div style={{ flex: 1 }}>
                          <strong>{file.name}</strong>{" "}
                          <small>({Math.round(file.size / 1024)} KB)</small>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          type="button"
                          onClick={() => removeAttachment(idx)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSendEmail}
              disabled={isEmailSending}
            >
              {isEmailSending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTaskDashboard;
